import { useState } from 'react';
import { api } from '../utils/api.js';

const LEVELS    = ['Level 1 Student','Level 2 Student','Level 3 Student','Funded Trader','Professional'];
const TIMEZONES = ['UTC','UTC+1','UTC+2','UTC+3','UTC+4','UTC+5','UTC+6','UTC-5','UTC-6','UTC-7','UTC-8',
                   'Africa/Kigali','Africa/Nairobi','Africa/Lagos','America/New_York','America/Chicago',
                   'America/Los_Angeles','Europe/London','Europe/Berlin','Asia/Dubai','Asia/Tokyo'];
const BROKERS   = ['FTMO','MyForexFunds','The5ers','IC Markets','Pepperstone','XM','Exness','Other'];

// ── Step header ──────────────────────────────────────────────
function Step({ num, total, title, subtitle }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,168,67,.5)', fontWeight: 600 }}>
          Step {num} of {total}
        </div>
        <div style={{ flex: 1, maxWidth: 80, height: 2, background: 'rgba(255,255,255,.07)', borderRadius: 1 }}>
          <div style={{ width: `${(num / total) * 100}%`, height: '100%', background: 'var(--gold)', borderRadius: 1, transition: 'width .4s' }} />
        </div>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--white)', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.6 }}>{subtitle}</div>
    </div>
  );
}

// ── Input wrappers ───────────────────────────────────────────
function OInput({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'rgba(240,234,214,.6)', marginBottom: 7 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'rgba(200,192,168,.3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select value={value} onChange={onChange}
      style={{ width: '100%', padding: '12px 14px', background: 'var(--dark3)', border: '1px solid rgba(255,255,255,.09)', color: 'var(--white)', fontSize: 13, outline: 'none', borderRadius: 'var(--radius)', appearance: 'none', cursor: 'pointer' }}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// ── Main ─────────────────────────────────────────────────────
export default function OnboardingPage({ session, onDone }) {
  const [step,   setStep]   = useState(1);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const TOTAL = 2;

  // Step 1 — profile only
  const [level,    setLevel]    = useState('Level 2 Student');
  const [timezone, setTimezone] = useState('UTC+2');
  const [broker,   setBroker]   = useState(BROKERS[0]);

  // Step 2 — trading rules (text rules, not risk numbers)
  const [rules,    setRules]    = useState(['Only trade during London/NY session', 'Risk max 1% per trade', 'No trades 30 min before high-impact news']);
  const [newRule,  setNewRule]  = useState('');

  const finish = async () => {
    setSaving(true);
    setError('');
    try {
      await api('/profile', {
        method: 'PUT',
        body: JSON.stringify({
          name:         session.name,
          level,
          timezone,
          broker:       broker || null,
          accType:      'Live',        // legacy field kept for compatibility
          tradingRules: rules.filter(r => r.trim()),
        }),
      });
      onDone();
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
      setSaving(false);
    }
  };

  const addRule = () => {
    if (newRule.trim() && rules.length < 10) {
      setRules(r => [...r, newRule.trim()]);
      setNewRule('');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      {/* Ambient glow */}
      <div style={{ position: 'absolute', top: '15%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle,rgba(212,168,67,.05),transparent 60%)', pointerEvents: 'none' }} />

      <div style={{ width: '100%', maxWidth: 500, position: 'relative', zIndex: 1 }}>

        {/* Greeting */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600, marginBottom: 4 }}>
            Welcome{session?.name ? `, ${session.name.split(' ')[0]}` : ''} 👋
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray)' }}>
            Quick setup — 1 minute and you're in.
          </div>
        </div>

        <div style={{ background: 'var(--dark2)', border: '1px solid rgba(212,168,67,.15)', padding: '32px 36px', boxShadow: '0 24px 60px rgba(0,0,0,.6)', borderRadius: 'var(--radius)' }}>

          {/* ── STEP 1: Profile ── */}
          {step === 1 && (
            <>
              <Step num={1} total={TOTAL}
                title="Your Trading Profile"
                subtitle="Tell us about yourself so we can tailor your dashboard." />

              <OInput label="Trading Level">
                <SelectInput value={level} onChange={e => setLevel(e.target.value)} options={LEVELS} />
              </OInput>

              <OInput label="Your Broker" hint="Optional — for your reference">
                <SelectInput value={broker} onChange={e => setBroker(e.target.value)} options={BROKERS} />
              </OInput>

              <OInput label="Your Timezone">
                <SelectInput value={timezone} onChange={e => setTimezone(e.target.value)} options={TIMEZONES} />
              </OInput>
            </>
          )}

          {/* ── STEP 2: Trading Rules ── */}
          {step === 2 && (
            <>
              <Step num={2} total={TOTAL}
                title="Your Trading Rules"
                subtitle="These appear as checkboxes when you log a trade. Keep them short and specific. You can edit them anytime in Settings." />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {rules.map((rule, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(212,168,67,.04)', border: '1px solid rgba(212,168,67,.12)', borderRadius: 'var(--radius)' }}>
                    <div style={{ width: 20, height: 20, background: 'rgba(212,168,67,.1)', border: '1px solid rgba(212,168,67,.2)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: 'var(--gold)', fontWeight: 700 }}>
                      {i + 1}
                    </div>
                    <input
                      value={rule}
                      onChange={e => { const r = [...rules]; r[i] = e.target.value; setRules(r); }}
                      style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--white)', fontSize: 13 }}
                    />
                    <button onClick={() => setRules(rules.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: 'rgba(224,85,85,.5)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(224,85,85,.5)'}>
                      ×
                    </button>
                  </div>
                ))}
                {rules.length === 0 && (
                  <div style={{ fontSize: 12, color: 'rgba(200,192,168,.3)', padding: '8px 0', fontStyle: 'italic' }}>
                    No rules yet. Add one below.
                  </div>
                )}
              </div>

              {rules.length < 10 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <input
                    value={newRule}
                    onChange={e => setNewRule(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRule()}
                    placeholder="e.g. Wait for session open"
                    style={{ flex: 1, padding: '10px 14px', background: 'var(--dark3)', border: '1px solid rgba(255,255,255,.09)', color: 'var(--white)', fontSize: 13, outline: 'none', borderRadius: 'var(--radius)' }}
                  />
                  <button onClick={addRule}
                    style={{ padding: '10px 18px', background: 'rgba(212,168,67,.12)', border: '1px solid rgba(212,168,67,.3)', color: 'var(--gold)', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 'var(--radius)', whiteSpace: 'nowrap' }}>
                    + Add
                  </button>
                </div>
              )}

              {/* Account setup teaser */}
              <div style={{ padding: '12px 14px', background: 'rgba(91,143,232,.06)', border: '1px solid rgba(91,143,232,.15)', borderRadius: 'var(--radius)', fontSize: 12, color: 'rgba(200,192,168,.55)', lineHeight: 1.7 }}>
                💡 <strong style={{ color: 'rgba(91,143,232,.9)' }}>Next:</strong> Once you're on the dashboard, add your trading accounts (live, demo, prop firm) from the <strong style={{ color: 'rgba(240,234,214,.6)' }}>My Accounts</strong> section. Each account gets its own balance and risk settings.
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(224,85,85,.08)', border: '1px solid rgba(224,85,85,.2)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--red)' }}>
              ⚠ {error}
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} disabled={saving}
                style={{ padding: '12px 22px', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(240,234,214,.5)', fontSize: 13, cursor: 'pointer', borderRadius: 'var(--radius)', transition: 'all .15s' }}>
                ← Back
              </button>
            )}
            <button
              onClick={() => step < TOTAL ? setStep(s => s + 1) : finish()}
              disabled={saving}
              style={{ flex: 1, padding: '13px', background: saving ? 'rgba(212,168,67,.3)' : 'linear-gradient(135deg,var(--gold),var(--gold-dim))', border: 'none', color: 'var(--black)', fontSize: 13, fontWeight: 700, letterSpacing: '.04em', cursor: saving ? 'default' : 'pointer', borderRadius: 'var(--radius)', transition: 'all .18s' }}>
              {saving ? 'Saving…' : step < TOTAL ? 'Continue →' : '🚀 Go to Dashboard'}
            </button>
          </div>

          {/* Skip */}
          {step < TOTAL && (
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <span onClick={saving ? undefined : finish}
                style={{ fontSize: 12, color: 'rgba(240,234,214,.25)', cursor: 'pointer', borderBottom: '1px solid rgba(240,234,214,.1)' }}>
                Skip setup for now
              </span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}